import { useEffect, useMemo, useState } from 'react';
import { Link } from '@tanstack/react-router';
import {
  Activity,
  Archive,
  ArchiveRestore,
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  CalendarClock,
  Clock,
  Copy,
  Eye,
  FolderTree,
  Gauge,
  ImageIcon,
  MessageSquare,
  Pin,
  Send,
  Tag as TagIcon,
  Trash2,
  Undo2,
  X,
  Zap,
} from 'lucide-react';
import type { PostListItem, SessionUser } from '@news-cms/shared';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { BulkCategoryDialog } from './bulk-category-dialog';
import { BulkScheduleDialog } from './bulk-schedule-dialog';
import { BulkTagDialog } from './bulk-tag-dialog';
import { PostStatusBadge } from './post-status-badge';
import { SetViewCountDialog } from './set-view-count-dialog';
import { toast } from '@/components/ui/toaster';
import { mediaAbsoluteUrl } from '@/lib/media';
import { hasPermission } from '@/lib/permissions';
import {
  type BulkPostAction,
  useBulkPostAction,
  useGeneratePreviewToken,
  useSetViewCount,
  useTogglePostFlag,
} from '@/lib/posts';

export type SortField = 'title' | 'updatedAt' | 'createdAt' | 'publishedAt';
export type SortDir = 'asc' | 'desc';

interface Props {
  items: PostListItem[];
  user: SessionUser | null;
  onDelete: (id: string) => void;
  isDeleting: boolean;
  /** When true, the row action becomes "restore" (undo soft-delete) and
   *  the confirm dialog asks about restoring instead of deleting. */
  trashMode?: boolean;
  onRestore?: (id: string) => void;
  isRestoring?: boolean;
  /** Hard-delete from trash. Only used in trashMode; otherwise the
   *  per-row icon is the soft-delete that calls `onDelete`. */
  onPurge?: (id: string) => void;
  isPurging?: boolean;
  /** Clone a post as a fresh draft. Per-row icon shown only when not in
   *  trash mode (trashed posts must be restored before duplicating). */
  onDuplicate?: (id: string) => void;
  isDuplicating?: boolean;
  /** Active sort — drives the header arrow indicator. */
  sortBy?: SortField;
  sortDir?: SortDir;
  /** Click handler for sortable headers; called with the field that was
   *  clicked. Parent decides the next dir (typically: same field flips,
   *  new field defaults to desc). Omit to render headers non-clickable. */
  onSortChange?: (field: SortField) => void;
}

function formatDateTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString('th-TH', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Drafts that haven't been touched in 14+ days are likely forgotten;
 * surface them with a small marker so editors notice during review.
 * Threshold matches the typical newsroom planning horizon — anything
 * older than 2 weeks is probably either dead or needs a refresh.
 */
const STALE_DRAFT_DAYS = 14;

function daysSince(iso: string): number {
  return Math.floor((Date.now() - new Date(iso).getTime()) / (24 * 60 * 60 * 1000));
}

function isStaleDraft(status: string, updatedAt: string): boolean {
  return status === 'draft' && daysSince(updatedAt) >= STALE_DRAFT_DAYS;
}

/**
 * Format an ISO timestamp in the future as a coarse human countdown:
 *   "X วัน"        when more than 24h away
 *   "Xh Ym"        when between 1h and 24h
 *   "X นาที"       when under an hour
 *   "พร้อมเผยแพร่"  when due (the cron fires every 5 min so this is a
 *                  brief window before the post flips to published)
 * Returns null for non-future timestamps + invalid input.
 */
function formatCountdown(iso: string, nowMs: number): string | null {
  const target = new Date(iso).getTime();
  if (Number.isNaN(target)) return null;
  const diff = target - nowMs;
  if (diff <= 0) return 'พร้อมเผยแพร่';
  const minutes = Math.floor(diff / 60_000);
  if (minutes < 60) return `${minutes} นาที`;
  const hours = Math.floor(minutes / 60);
  const remMin = minutes - hours * 60;
  if (hours < 24) return `${hours}h ${remMin}m`;
  const days = Math.floor(hours / 24);
  return `${days} วัน`;
}

/**
 * Tiny "ticking now()" hook so countdown badges refresh without
 * forcing a list refetch. 30-second cadence is plenty for minute-
 * level granularity and stays cheap even with 100 rows on screen.
 */
function useTickingNow(): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(id);
  }, []);
  return now;
}

/**
 * Column-header button for sort-aware columns. Shows an arrow indicator
 * when this is the active sort, a faded double-arrow when it's clickable
 * but inactive. Falls back to plain text when onSortChange is omitted
 * (e.g. table mounted without a sort-controlling parent).
 */
function SortableHeader({
  label,
  field,
  activeField,
  activeDir,
  onSort,
}: {
  label: string;
  field: SortField;
  activeField: SortField | undefined;
  activeDir: SortDir | undefined;
  onSort: ((field: SortField) => void) | undefined;
}) {
  if (!onSort) return <>{label}</>;
  const isActive = activeField === field;
  const Icon = !isActive ? ArrowUpDown : activeDir === 'asc' ? ArrowUp : ArrowDown;
  return (
    <button
      type="button"
      onClick={() => onSort(field)}
      className="inline-flex items-center gap-1 hover:text-slate-700 dark:hover:text-slate-200"
    >
      {label}
      <Icon className={isActive ? 'h-3 w-3' : 'h-3 w-3 opacity-40'} />
    </button>
  );
}

const ACTION_LABEL: Record<BulkPostAction, string> = {
  publish: 'เผยแพร่',
  unpublish: 'ถอนการเผยแพร่',
  delete: 'ลบ',
  restore: 'กู้คืน',
  purge: 'ลบถาวร',
  // Schedule has its own dialog (BulkScheduleDialog) and never goes
  // through the generic ConfirmDialog → this label is unused at
  // runtime but required by the Record type completeness.
  schedule: 'ตั้งเวลาเผยแพร่',
  archive: 'เก็บถาวร',
  unarchive: 'เลิกเก็บถาวร',
};

export function PostListTable({
  items,
  user,
  onDelete,
  isDeleting,
  trashMode = false,
  onRestore,
  isRestoring = false,
  onPurge,
  isPurging = false,
  onDuplicate,
  isDuplicating = false,
  sortBy,
  sortDir,
  onSortChange,
}: Props) {
  const canDelete = hasPermission(user, 'post.delete');
  const canPublish = hasPermission(user, 'post.publish');
  const canUnpublish = hasPermission(user, 'post.unpublish');
  const canCreate = hasPermission(user, 'post.create');
  const canUpdate = hasPermission(user, 'post.update');
  const canUpdateOwn = hasPermission(user, 'post.update_own');
  const canManageTag = hasPermission(user, 'tag.manage');
  const canManageCategory = hasPermission(user, 'category.manage');
  const canReadAudit = hasPermission(user, 'audit.read');
  // Manual view-count edit is super_admin-only (matches the API gate) —
  // it rewrites a public analytics figure, so it's not a normal permission.
  const isSuperAdmin = user?.role.slug === 'super_admin';
  // Bulk shows different actions in trash (restore/purge) vs live
  // (publish/unpublish/delete + bulk-tag) — both use the same
  // select-checkbox UI, gated on the relevant permission either way.
  const canSelect = trashMode ? canDelete : canDelete || canPublish || canUnpublish || canUpdate;
  // Action column visibility: shown in trash (restore+purge) iff canDelete;
  // shown in live mode iff canDelete OR any per-row action could fire
  // (preview/duplicate/audit). Authors with post.update_own still see preview.
  const showActionsColumn = trashMode
    ? canDelete
    : canDelete || canCreate || canUpdate || canUpdateOwn || canReadAudit || isSuperAdmin;

  const [pendingDelete, setPendingDelete] = useState<PostListItem | null>(null);
  const [pendingRestore, setPendingRestore] = useState<PostListItem | null>(null);
  const [pendingPurge, setPendingPurge] = useState<PostListItem | null>(null);
  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  const [pendingBulk, setPendingBulk] = useState<BulkPostAction | null>(null);
  const [bulkTagOpen, setBulkTagOpen] = useState(false);
  const [bulkCategoryOpen, setBulkCategoryOpen] = useState(false);
  const [bulkScheduleOpen, setBulkScheduleOpen] = useState(false);
  const [pendingViewCount, setPendingViewCount] = useState<PostListItem | null>(null);
  const bulk = useBulkPostAction();
  const toggleFlag = useTogglePostFlag();
  const generatePreview = useGeneratePreviewToken();
  const setViewCount = useSetViewCount();
  const nowMs = useTickingNow();

  /**
   * One-shot preview open: mint a token, then pop a new tab at
   * /preview/<token>. We don't reserve the popup ahead of time —
   * the mint round-trip is fast (<300ms typical) and modern
   * browsers usually allow window.open from the user-event chain.
   * A loading toast covers the wait so it never feels like a no-op.
   */
  const openPreview = (id: string) => {
    const tid = toast.loading('กำลังสร้างลิงก์พรีวิว...');
    generatePreview.mutate(id, {
      onSuccess: (data) => {
        toast.dismiss(tid);
        const url = `${window.location.origin}/preview/${data.token}`;
        window.open(url, '_blank', 'noopener,noreferrer');
      },
      onError: () => {
        toast.dismiss(tid);
        // Hook itself already shows a destructive toast via onError —
        // no extra surface needed here.
      },
    });
  };

  const allOnPageIds = useMemo(() => items.map((p) => p.id), [items]);
  const allOnPageSelected = allOnPageIds.length > 0 && allOnPageIds.every((id) => selected.has(id));
  const someSelected = selected.size > 0;

  const toggleOne = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAllOnPage = () => {
    setSelected((prev) => {
      if (allOnPageSelected) {
        const next = new Set(prev);
        for (const id of allOnPageIds) next.delete(id);
        return next;
      }
      const next = new Set(prev);
      for (const id of allOnPageIds) next.add(id);
      return next;
    });
  };

  const clearSelection = () => setSelected(new Set());

  const handleConfirmDelete = () => {
    if (!pendingDelete) return;
    onDelete(pendingDelete.id);
    setPendingDelete(null);
  };

  const handleConfirmRestore = () => {
    if (!pendingRestore || !onRestore) return;
    onRestore(pendingRestore.id);
    setPendingRestore(null);
  };

  const handleConfirmPurge = () => {
    if (!pendingPurge || !onPurge) return;
    onPurge(pendingPurge.id);
    setPendingPurge(null);
  };

  const handleBulkConfirm = () => {
    if (!pendingBulk || selected.size === 0) return;
    const ids = Array.from(selected);
    bulk.mutate(
      { ids, action: pendingBulk },
      {
        onSuccess: () => {
          clearSelection();
          setPendingBulk(null);
        },
        onError: () => setPendingBulk(null),
      },
    );
  };

  return (
    <>
      {someSelected && (
        <div className="bg-brand-50 dark:bg-brand-900/30 flex flex-wrap items-center gap-3 rounded-lg border border-slate-200 p-3 dark:border-slate-800">
          <span className="text-sm font-medium">เลือก {selected.size} บทความ</span>
          <div className="flex flex-wrap gap-2">
            {trashMode ? (
              <>
                {canDelete && (
                  <Button
                    type="button"
                    variant="default"
                    size="sm"
                    disabled={bulk.isPending}
                    onClick={() => setPendingBulk('restore')}
                  >
                    <Undo2 className="mr-1.5 h-3.5 w-3.5" />
                    กู้คืน
                  </Button>
                )}
                {canDelete && (
                  <Button
                    type="button"
                    variant="destructive"
                    size="sm"
                    disabled={bulk.isPending}
                    onClick={() => setPendingBulk('purge')}
                  >
                    <Trash2 className="mr-1.5 h-3.5 w-3.5" />
                    ลบถาวร
                  </Button>
                )}
              </>
            ) : (
              <>
                {canPublish && (
                  <Button
                    type="button"
                    variant="default"
                    size="sm"
                    disabled={bulk.isPending}
                    onClick={() => setPendingBulk('publish')}
                  >
                    <Send className="mr-1.5 h-3.5 w-3.5" />
                    เผยแพร่
                  </Button>
                )}
                {canPublish && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={bulk.isPending}
                    onClick={() => setBulkScheduleOpen(true)}
                  >
                    <CalendarClock className="mr-1.5 h-3.5 w-3.5" />
                    ตั้งเวลา...
                  </Button>
                )}
                {canUnpublish && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={bulk.isPending}
                    onClick={() => setPendingBulk('unpublish')}
                  >
                    <Undo2 className="mr-1.5 h-3.5 w-3.5" />
                    ถอนเผยแพร่
                  </Button>
                )}
                {canUpdate && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={bulk.isPending}
                    onClick={() => setBulkTagOpen(true)}
                  >
                    <TagIcon className="mr-1.5 h-3.5 w-3.5" />
                    แท็ก...
                  </Button>
                )}
                {canUpdate && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={bulk.isPending}
                    onClick={() => setBulkCategoryOpen(true)}
                  >
                    <FolderTree className="mr-1.5 h-3.5 w-3.5" />
                    หมวด...
                  </Button>
                )}
                {/* Archive / Unarchive — server enforces per-row, returns
                    409 on no-ops which gets surfaced via the rejected
                    counter. Both buttons stay visible so selections with
                    mixed statuses still work in a single click each. */}
                {canUpdate && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={bulk.isPending}
                    onClick={() => setPendingBulk('archive')}
                  >
                    <Archive className="mr-1.5 h-3.5 w-3.5" />
                    เก็บถาวร
                  </Button>
                )}
                {canUpdate && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={bulk.isPending}
                    onClick={() => setPendingBulk('unarchive')}
                  >
                    <ArchiveRestore className="mr-1.5 h-3.5 w-3.5" />
                    เลิกเก็บถาวร
                  </Button>
                )}
                {canDelete && (
                  <Button
                    type="button"
                    variant="destructive"
                    size="sm"
                    disabled={bulk.isPending}
                    onClick={() => setPendingBulk('delete')}
                  >
                    <Trash2 className="mr-1.5 h-3.5 w-3.5" />
                    ลบ
                  </Button>
                )}
              </>
            )}
          </div>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={clearSelection}
            disabled={bulk.isPending}
            className="ml-auto"
          >
            <X className="mr-1.5 h-3.5 w-3.5" />
            ล้างเลือก
          </Button>
        </div>
      )}

      <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500 dark:bg-slate-800/50 dark:text-slate-400">
            <tr>
              {canSelect && (
                <th className="w-10 px-4 py-3 font-medium">
                  <input
                    type="checkbox"
                    aria-label="เลือกทั้งหน้า"
                    checked={allOnPageSelected}
                    onChange={toggleAllOnPage}
                    className="h-4 w-4"
                  />
                </th>
              )}
              <th className="w-20 px-4 py-3 font-medium" />
              <th className="px-4 py-3 font-medium">
                <SortableHeader
                  label="หัวข้อ"
                  field="title"
                  activeField={sortBy}
                  activeDir={sortDir}
                  onSort={onSortChange}
                />
              </th>
              <th className="px-4 py-3 font-medium">สถานะ</th>
              <th className="px-4 py-3 font-medium">ผู้เขียน</th>
              <th className="px-4 py-3 font-medium">
                <SortableHeader
                  label="แก้ไขล่าสุด"
                  field="updatedAt"
                  activeField={sortBy}
                  activeDir={sortDir}
                  onSort={onSortChange}
                />
              </th>
              {showActionsColumn && <th className="w-28 px-4 py-3 font-medium" />}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
            {items.map((p) => {
              const isSelected = selected.has(p.id);
              return (
                <tr
                  key={p.id}
                  className={
                    isSelected
                      ? 'bg-brand-50/50 dark:bg-brand-900/20'
                      : 'hover:bg-slate-50 dark:hover:bg-slate-800/50'
                  }
                >
                  {canSelect && (
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        aria-label={`เลือก ${p.title}`}
                        checked={isSelected}
                        onChange={() => toggleOne(p.id)}
                        className="h-4 w-4"
                      />
                    </td>
                  )}
                  <td className="px-4 py-3">
                    <Link
                      to="/posts/$postId"
                      params={{ postId: p.id }}
                      className="block h-10 w-[60px] overflow-hidden rounded border border-slate-200 bg-slate-100 dark:border-slate-800 dark:bg-slate-800"
                      aria-label={`Cover ของ ${p.title}`}
                    >
                      {p.featuredImage ? (
                        <img
                          src={mediaAbsoluteUrl(p.featuredImage.url)}
                          alt={p.featuredImage.altText ?? ''}
                          loading="lazy"
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        <div className="flex h-full w-full items-center justify-center text-slate-300">
                          <ImageIcon className="h-4 w-4" />
                        </div>
                      )}
                    </Link>
                  </td>
                  <td className="px-4 py-3">
                    <Link
                      to="/posts/$postId"
                      params={{ postId: p.id }}
                      className="hover:text-brand-600 dark:hover:text-brand-400 font-medium text-slate-900 dark:text-slate-50"
                    >
                      {p.title}
                    </Link>
                    {/* Fresh-content pill — < 24h old. Helps editors
                        scanning a long list spot today's incoming
                        without comparing timestamps in their head. */}
                    {Date.now() - new Date(p.createdAt).getTime() < 24 * 60 * 60 * 1000 && (
                      <span
                        className="ml-1.5 inline-flex items-center rounded-full bg-emerald-100 px-1.5 py-0.5 align-middle text-[10px] font-medium text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300"
                        title={`สร้างเมื่อ ${new Date(p.createdAt).toLocaleString('th-TH', { hour: '2-digit', minute: '2-digit' })}`}
                      >
                        ใหม่
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5">
                      <PostStatusBadge status={p.status} />
                      {canUpdate && (
                        <>
                          <button
                            type="button"
                            disabled={toggleFlag.isPending}
                            onClick={() =>
                              toggleFlag.mutate({
                                id: p.id,
                                flag: 'isSticky',
                                value: !p.isSticky,
                              })
                            }
                            aria-pressed={p.isSticky}
                            title={p.isSticky ? 'เลิกปักหมุด' : 'ปักหมุด'}
                            className={
                              p.isSticky
                                ? 'inline-flex h-5 w-5 items-center justify-center rounded-full bg-blue-100 text-blue-700 hover:bg-blue-200 dark:bg-blue-900/40 dark:text-blue-300'
                                : 'inline-flex h-5 w-5 items-center justify-center rounded-full text-slate-300 hover:bg-slate-100 hover:text-slate-600 dark:text-slate-700 dark:hover:bg-slate-800 dark:hover:text-slate-300'
                            }
                          >
                            <Pin className="h-3 w-3" />
                          </button>
                          <button
                            type="button"
                            disabled={toggleFlag.isPending}
                            onClick={() =>
                              toggleFlag.mutate({
                                id: p.id,
                                flag: 'isBreaking',
                                value: !p.isBreaking,
                              })
                            }
                            aria-pressed={p.isBreaking}
                            title={p.isBreaking ? 'เลิกข่าวด่วน' : 'ตั้งเป็นข่าวด่วน'}
                            className={
                              p.isBreaking
                                ? 'inline-flex h-5 w-5 items-center justify-center rounded-full bg-red-100 text-red-700 hover:bg-red-200 dark:bg-red-900/40 dark:text-red-300'
                                : 'inline-flex h-5 w-5 items-center justify-center rounded-full text-slate-300 hover:bg-slate-100 hover:text-slate-600 dark:text-slate-700 dark:hover:bg-slate-800 dark:hover:text-slate-300'
                            }
                          >
                            <Zap className="h-3 w-3" />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    {/* Click author name → filter the list to that
                        author's posts. Standalone Link with a static
                        search resets other filters intentionally —
                        layering would surprise editors who expect
                        "ดูบทความคนนี้" to mean only that. */}
                    <Link
                      to="/posts"
                      search={{ authorId: p.authorId }}
                      className="hover:text-brand-600 dark:hover:text-brand-400 text-slate-600 hover:underline dark:text-slate-400"
                      title={`ดูบทความของ ${p.authorName}`}
                    >
                      {p.authorName}
                    </Link>
                    {/* "ของฉัน" hint for fast self-scan in shared
                        lists. Different gesture from clicking the
                        author name (which filters to all-mine) —
                        this stays passive. */}
                    {user && p.authorId === user.id && (
                      <span className="ml-1.5 rounded bg-slate-200 px-1 py-0.5 text-[10px] text-slate-600 dark:bg-slate-800 dark:text-slate-400">
                        ฉัน
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-slate-500 dark:text-slate-500">
                    <div className="flex items-center gap-1.5">
                      <span>{formatDateTime(p.updatedAt)}</span>
                      {isStaleDraft(p.status, p.updatedAt) && (
                        <span
                          className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-800 dark:bg-amber-900/30 dark:text-amber-300"
                          title={`ฉบับร่างค้างมา ${daysSince(p.updatedAt)} วัน`}
                        >
                          ค้าง {daysSince(p.updatedAt)}d
                        </span>
                      )}
                      {/* Scheduled countdown — only renders when status is
                          scheduled AND scheduledAt is set. Falls quiet
                          immediately once the cron flips the post to
                          published. */}
                      {p.status === 'scheduled' &&
                        p.scheduledAt &&
                        (() => {
                          const label = formatCountdown(p.scheduledAt, nowMs);
                          if (!label) return null;
                          return (
                            <span
                              className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-1.5 py-0.5 text-[10px] font-medium text-blue-800 dark:bg-blue-900/30 dark:text-blue-300"
                              title={`เผยแพร่ ${formatDateTime(p.scheduledAt)}`}
                            >
                              <Clock className="h-2.5 w-2.5" />
                              {label === 'พร้อมเผยแพร่' ? label : `เผยแพร่ใน ${label}`}
                            </span>
                          );
                        })()}
                    </div>
                    {p.readingTimeMinutes > 0 && (
                      <div className="text-[10px] text-slate-400">
                        อ่าน ~{p.readingTimeMinutes} นาที · {p.wordCount} คำ
                      </div>
                    )}
                    {p.viewCount > 0 && (
                      <div
                        className="mt-0.5 inline-flex items-center gap-1 text-[10px] text-slate-500 dark:text-slate-400"
                        title="ยอดผู้เข้าชมทั้งหมด"
                      >
                        <Eye className="h-2.5 w-2.5" />
                        {p.viewCount.toLocaleString()} วิว
                      </div>
                    )}
                    {p.commentCount > 0 && (
                      <Link
                        to="/comments"
                        search={{ postId: p.id }}
                        className="mt-0.5 inline-flex items-center gap-1 text-[10px] text-slate-500 hover:text-blue-600 dark:text-slate-400 dark:hover:text-blue-400"
                        title="ดูคอมเมนต์ของโพสต์นี้"
                      >
                        <MessageSquare className="h-2.5 w-2.5" />
                        {p.commentCount} คอมเมนต์
                      </Link>
                    )}
                  </td>
                  {showActionsColumn && (
                    <td className="px-4 py-3 text-right">
                      {trashMode ? (
                        <div className="flex items-center justify-end gap-1">
                          {canDelete && (
                            <>
                              <Button
                                variant="ghost"
                                size="icon"
                                disabled={isRestoring}
                                onClick={() => setPendingRestore(p)}
                                aria-label="กู้คืน"
                              >
                                <Undo2 className="text-brand-600 h-4 w-4" />
                              </Button>
                              {onPurge && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  disabled={isPurging}
                                  onClick={() => setPendingPurge(p)}
                                  aria-label="ลบถาวร"
                                >
                                  <Trash2 className="h-4 w-4 text-red-500" />
                                </Button>
                              )}
                            </>
                          )}
                        </div>
                      ) : (
                        <div className="flex items-center justify-end gap-1">
                          {/* Preview: per-row eye opens /preview/<token> in
                              a new tab. Same permission gate as the token
                              mint endpoint (post.update OR update_own +
                              author match) so unauthorized rows just hide
                              the button instead of producing 403 toasts. */}
                          {(canUpdate || (canUpdateOwn && p.authorId === user?.id)) && (
                            <Button
                              variant="ghost"
                              size="icon"
                              disabled={generatePreview.isPending}
                              onClick={() => openPreview(p.id)}
                              aria-label="ดูพรีวิว"
                              title="เปิดพรีวิวในแท็บใหม่"
                            >
                              <Eye className="h-4 w-4 text-slate-500" />
                            </Button>
                          )}
                          {/* Audit drill — same gesture as the
                              PostAuditPanel "ดูทั้งหมด" link on the
                              editor, but reachable straight from the
                              list. Useful for "who edited this and
                              when?" without entering the editor. */}
                          {canReadAudit && (
                            <Link
                              to="/audit"
                              search={{ resourceType: 'post', resourceId: p.id }}
                              className="inline-flex h-9 w-9 items-center justify-center rounded-md text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"
                              title="ดู audit ของบทความนี้"
                              aria-label="ดู audit"
                            >
                              <Activity className="h-4 w-4" />
                            </Link>
                          )}
                          {canCreate && onDuplicate && (
                            <Button
                              variant="ghost"
                              size="icon"
                              disabled={isDuplicating}
                              onClick={() => onDuplicate(p.id)}
                              aria-label="ทำสำเนา"
                              title="ทำสำเนา"
                            >
                              <Copy className="h-4 w-4 text-slate-500" />
                            </Button>
                          )}
                          {/* Super-admin only: manually set/bump the view
                              count. Gated identically to the API endpoint. */}
                          {isSuperAdmin && (
                            <Button
                              variant="ghost"
                              size="icon"
                              disabled={setViewCount.isPending}
                              onClick={() => setPendingViewCount(p)}
                              aria-label="แก้ยอดวิว"
                              title="แก้ยอดวิว"
                            >
                              <Gauge className="h-4 w-4 text-slate-500" />
                            </Button>
                          )}
                          {canDelete && (
                            <Button
                              variant="ghost"
                              size="icon"
                              disabled={isDeleting}
                              onClick={() => setPendingDelete(p)}
                              aria-label="ลบ"
                            >
                              <Trash2 className="h-4 w-4 text-red-500" />
                            </Button>
                          )}
                        </div>
                      )}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <ConfirmDialog
        open={pendingDelete !== null}
        onOpenChange={(open) => {
          if (!open) setPendingDelete(null);
        }}
        title="ยืนยันการลบ"
        description={
          pendingDelete
            ? `จะย้าย "${pendingDelete.title}" ไปถังขยะ — กู้คืนได้จากแท็บ "ถังขยะ" ภายหลัง`
            : undefined
        }
        confirmLabel="ลบ"
        variant="destructive"
        onConfirm={handleConfirmDelete}
        isLoading={isDeleting}
      />

      <ConfirmDialog
        open={pendingRestore !== null}
        onOpenChange={(open) => {
          if (!open) setPendingRestore(null);
        }}
        title="กู้คืนบทความ"
        description={
          pendingRestore
            ? `จะนำ "${pendingRestore.title}" กลับมาในรายการบทความ — สถานะเดิมจะถูกคงไว้`
            : undefined
        }
        confirmLabel="กู้คืน"
        onConfirm={handleConfirmRestore}
        isLoading={isRestoring}
      />

      <ConfirmDialog
        open={pendingPurge !== null}
        onOpenChange={(open) => {
          if (!open) setPendingPurge(null);
        }}
        title="ลบถาวร — กู้คืนไม่ได้"
        description={
          pendingPurge
            ? `"${pendingPurge.title}" จะถูกลบจากระบบอย่างถาวร พร้อมหมวด/แท็ก/รูป/revisions ที่เกี่ยวข้อง — กู้คืนไม่ได้`
            : undefined
        }
        confirmLabel="ลบถาวร"
        variant="destructive"
        onConfirm={handleConfirmPurge}
        isLoading={isPurging}
      />

      <ConfirmDialog
        open={pendingBulk !== null}
        onOpenChange={(open) => {
          if (!open) setPendingBulk(null);
        }}
        title={
          pendingBulk === 'purge'
            ? `ลบถาวร ${selected.size} บทความ — กู้คืนไม่ได้`
            : pendingBulk
              ? `ยืนยัน${ACTION_LABEL[pendingBulk]} ${selected.size} บทความ`
              : ''
        }
        description={
          pendingBulk === 'delete'
            ? 'บทความที่เลือกจะถูกย้ายไปถังขยะทั้งหมด'
            : pendingBulk === 'purge'
              ? 'บทความที่เลือกจะถูกลบจากระบบอย่างถาวร พร้อมหมวด/แท็ก/รูป/revisions ที่เกี่ยวข้อง — กู้คืนไม่ได้'
              : pendingBulk
                ? `จะ${ACTION_LABEL[pendingBulk]}ทุกบทความที่เลือก (บางรายการอาจล้มเหลวถ้าสิทธิ์ไม่พอ)`
                : ''
        }
        confirmLabel={pendingBulk ? ACTION_LABEL[pendingBulk] : ''}
        variant={pendingBulk === 'delete' || pendingBulk === 'purge' ? 'destructive' : 'default'}
        onConfirm={handleBulkConfirm}
        isLoading={bulk.isPending}
      />

      <BulkTagDialog
        open={bulkTagOpen}
        onOpenChange={setBulkTagOpen}
        postIds={Array.from(selected)}
        canCreate={canManageTag}
        onApplied={clearSelection}
      />

      <BulkCategoryDialog
        open={bulkCategoryOpen}
        onOpenChange={setBulkCategoryOpen}
        postIds={Array.from(selected)}
        canCreate={canManageCategory}
        onApplied={clearSelection}
      />

      <BulkScheduleDialog
        open={bulkScheduleOpen}
        onOpenChange={setBulkScheduleOpen}
        postCount={selected.size}
        isPending={bulk.isPending}
        onConfirm={(scheduledAt) => {
          bulk.mutate(
            { ids: Array.from(selected), action: 'schedule', scheduledAt },
            {
              onSuccess: () => {
                clearSelection();
                setBulkScheduleOpen(false);
              },
              onError: () => setBulkScheduleOpen(false),
            },
          );
        }}
      />

      <SetViewCountDialog
        open={pendingViewCount !== null}
        onOpenChange={(open) => {
          if (!open) setPendingViewCount(null);
        }}
        post={pendingViewCount}
        isPending={setViewCount.isPending}
        onConfirm={(input) => {
          if (!pendingViewCount) return;
          setViewCount.mutate(
            { id: pendingViewCount.id, ...input },
            { onSuccess: () => setPendingViewCount(null) },
          );
        }}
      />
    </>
  );
}
